/**
 * Settings › Mail — what this tenant's alerts look like when they arrive.
 *
 * ⚠️ The most useful thing on this screen is the sentence explaining what
 * cannot be changed. Decision 6 (PLAN-2026-09-15 §5a, `src/notify/sender.ts`)
 * fixes the sending ADDRESS to one the platform controls, because per-customer
 * From addresses need SPF and DKIM alignment in the customer's own DNS and the
 * failure when that lapses is an outage notification in a junk folder. A screen
 * that simply omitted the address would leave an administrator hunting for a
 * field that does not exist; this one shows the address, greyed, and says why.
 *
 * So the two editable fields are the ones that cost nothing to give away: the
 * name in front of the address, and where a reply goes.
 *
 * 🔴 The preview is built from the API's own `effective` values, not recomputed
 * here. The fallbacks — mail name to product name, reply address to support
 * address, the platform's `COSMON` default meaning "unset" — live in
 * `senderIdentityFrom` on the server, and a second copy in the browser would be
 * a second thing to keep in step. What the preview shows is what the server
 * says it will send.
 */

import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from './api.ts';
import { Banner, Button, Field, Loading, Section } from './components/index.ts';

const failure = (err: unknown): string => (err instanceof ApiError ? err.message : String(err));

interface MailIdentity {
  productName: string | null;
  emailFromName: string | null;
  emailReplyTo: string | null;
  supportEmail: string | null;
  effective: { displayName: string | null; replyTo: string | null };
  /** Null when this deployment has no relay configured and sends nothing. */
  senderAddress: string | null;
  canChange: boolean;
}

/**
 * One sentence saying what a recipient will see.
 *
 * ⚠️ It reads the same on a workstation with no relay as on production, because
 * the identity is the same either way — only the sending is missing. An earlier
 * version replaced the whole sentence with "no relay configured", which hid the
 * name and reply address on exactly the machine where somebody is setting them
 * up for the first time.
 */
function summarise(identity: MailIdentity): string {
  const name = identity.effective.displayName;
  const sentAs =
    identity.senderAddress === null
      ? `Sent as ${name ?? 'the platform'}, once a mail relay is configured on this deployment`
      : `Sent as ${name === null ? identity.senderAddress : `${name} <${identity.senderAddress}>`}`;
  const replies =
    identity.effective.replyTo === null
      ? 'No reply address is set, so a reply goes nowhere.'
      : `Replies go to ${identity.effective.replyTo}.`;
  return `${sentAs}. ${replies}`;
}

interface Schedule {
  hour: number;
  timeZone: string;
  reports: Array<{ name: string; key: string }>;
  canChange: boolean;
}

/** What each routing key is called on this screen, so the hour names its work. */
const REPORT_LABELS: Record<string, string> = {
  'report:daily': 'Fleet report',
  'report:sd-digest': 'Footage loss digest',
  'report:storage-weekly': 'Weekly storage report (Mondays)',
};

/**
 * When the scheduled reports go out.
 *
 * ⚠️ The timezone is a plain text field rather than a picker. A select listing
 * every IANA zone is four hundred options nobody scrolls, and this changes
 * about once in a tenant's life. The server refuses a name it does not know and
 * says what a good one looks like, which is the part that matters.
 */
export function ReportScheduleView(): React.JSX.Element {
  const [state, setState] = useState<Schedule | null>(null);
  const [hour, setHour] = useState(7);
  const [zone, setZone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await api.get<Schedule>('/api/tenant/reports');
      setState(next);
      setHour(next.hour);
      setZone(next.timeZone);
    } catch (err) {
      setError(failure(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null && state === null) return <Banner tone="fault">{error}</Banner>;
  if (state === null) return <Loading rows={2} cols={1} label="Loading the report schedule" />;

  const schedule = state;
  const dirty = hour !== schedule.hour || zone.trim() !== schedule.timeZone;

  return (
    <Section
      title="When reports go out"
      summary={`${String(hour).padStart(2, '0')}:00 ${schedule.timeZone}. A report that is late goes out late rather than not at all.`}
    >
      <div className="form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {done !== null && <Banner tone="ok">{done}</Banner>}

        <Field id="report-hour" label="Hour" hint="Local time, on a 24-hour clock.">
          <select
            id="report-hour"
            value={String(hour)}
            disabled={!schedule.canChange}
            onChange={(event) => {
              setHour(Number(event.target.value));
              setDone(null);
            }}
          >
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={String(h)}>{`${String(h).padStart(2, '0')}:00`}</option>
            ))}
          </select>
        </Field>

        <Field
          id="report-timezone"
          label="Timezone"
          hint="An IANA name, such as Australia/Sydney or Australia/Brisbane."
        >
          <input
            id="report-timezone"
            type="text"
            value={zone}
            disabled={!schedule.canChange}
            onChange={(event) => {
              setZone(event.target.value);
              setDone(null);
            }}
          />
        </Field>

        <p className="small muted">
          Governs {schedule.reports.map((r) => REPORT_LABELS[r.key] ?? r.key).join(', ')}. Switch one
          off under Alert routing.
        </p>

        {!schedule.canChange ? (
          <p className="small muted">Owners and administrators can change this.</p>
        ) : (
          <Button
            variant="primary"
            busy={busy}
            disabled={!dirty}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.put('/api/tenant/reports', { hour, timeZone: zone.trim() });
                setDone('Saved.');
                await load();
              } catch (err) {
                setError(failure(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            Save schedule
          </Button>
        )}
      </div>
    </Section>
  );
}

export function MailIdentityView(): React.JSX.Element {
  const [state, setState] = useState<MailIdentity | null>(null);
  const [name, setName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await api.get<MailIdentity>('/api/tenant/branding');
      setState(next);
      setName(next.emailFromName ?? '');
      setReplyTo(next.emailReplyTo ?? '');
    } catch (err) {
      setError(failure(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null && state === null) return <Banner tone="fault">{error}</Banner>;
  if (state === null) return <Loading rows={3} cols={1} label="Loading this tenant's mail identity" />;

  const identity = state;
  const dirty =
    name.trim() !== (identity.emailFromName ?? '') || replyTo.trim() !== (identity.emailReplyTo ?? '');

  return (
    <Section
      title="How your alerts arrive"
      summary={summarise(identity)}
    >
      <div className="form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {done !== null && <Banner tone="ok">{done}</Banner>}

        <Field
          id="mail-sender-address"
          label="Sent from"
          hint={
            'Fixed for every customer on this platform. Sending from your own domain would need ' +
            'SPF and DKIM records in your DNS, re-proved whenever you change mail provider — ' +
            'and when that lapses, alerts stop arriving instead of failing visibly. Ask us if ' +
            'you need it and can publish those records.'
          }
        >
          <input
            id="mail-sender-address"
            type="text"
            readOnly
            disabled
            value={identity.senderAddress ?? 'No relay configured on this deployment'}
          />
        </Field>

        <Field
          id="mail-from-name"
          label="Name shown to recipients"
          hint={
            identity.productName === null || identity.productName === 'COSMON'
              ? 'Leave empty and mail carries no name, just the address.'
              : `Leave empty and mail uses your product name, ${identity.productName}.`
          }
        >
          <input
            id="mail-from-name"
            type="text"
            maxLength={120}
            value={name}
            disabled={!identity.canChange}
            onChange={(event) => {
              setName(event.target.value);
              setDone(null);
            }}
          />
        </Field>

        <Field
          id="mail-reply-to"
          label="Replies go to"
          error={fieldError}
          hint={
            identity.supportEmail === null
              ? 'Leave empty and replies go nowhere — the sending address is not a mailbox anyone reads.'
              : `Leave empty and replies go to your support address, ${identity.supportEmail}.`
          }
        >
          <input
            id="mail-reply-to"
            type="email"
            value={replyTo}
            disabled={!identity.canChange}
            onChange={(event) => {
              setReplyTo(event.target.value);
              setFieldError(null);
              setDone(null);
            }}
          />
        </Field>

        {!identity.canChange ? (
          <p className="small muted">Owners and administrators can change this.</p>
        ) : (
          <Button
            variant="primary"
            busy={busy}
            disabled={!dirty}
            onClick={async () => {
              setBusy(true);
              setError(null);
              setFieldError(null);
              try {
                await api.put('/api/tenant/branding', {
                  emailFromName: name.trim(),
                  emailReplyTo: replyTo.trim(),
                });
                setDone('Saved. New alerts will carry this.');
                await load();
              } catch (err) {
                const message = failure(err);
                // A rejected reply address belongs against the field that holds
                // it, not in a banner at the top the person has to map back.
                if (message.includes('emailReplyTo')) setFieldError(message);
                else setError(message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Save sender
          </Button>
        )}
      </div>
    </Section>
  );
}
